import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const FEMALE_NAMES = ['Valentina R.', 'Mariana G.', 'Sofía P.', 'Camila T.', 'Isabella M.', 'Renata L.', 'Lucía S.', 'Daniela F.', 'Carolina V.', 'Gabriela N.', 'Alejandra C.', 'Natalia B.', 'Andrea H.', 'Paula D.', 'Fernanda K.', 'Victoria J.', 'Juliana W.', 'Catalina Q.', 'Martina Z.', 'Elena Y.'];
interface Purchase {
  name: string;
  price: string;
}
export function RecentPurchasePopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentPurchase, setCurrentPurchase] = useState<Purchase | null>(null);
  const [usedNames, setUsedNames] = useState<string[]>([]);
  const [isClosed, setIsClosed] = useState(false);
  // Check if user closed it before
  useEffect(() => {
    const closed = sessionStorage.getItem('recentPurchasePopupClosed');
    if (closed === 'true') {
      setIsClosed(true);
    }
  }, []);
  // Generate random purchase
  const generatePurchase = (): Purchase => {
    // Get available names (not used yet)
    const availableNames = FEMALE_NAMES.filter(name => !usedNames.includes(name));
    // If all names used, reset
    const namePool = availableNames.length > 0 ? availableNames : FEMALE_NAMES;
    // Random name
    const randomName = namePool[Math.floor(Math.random() * namePool.length)];
    // Random price between $7.97 and $26.97
    const randomPrice = (Math.random() * (26.97 - 7.97) + 7.97).toFixed(2);
    return {
      name: randomName,
      price: randomPrice
    };
  };
  // Show popup with realistic timing
  useEffect(() => {
    if (isClosed) return;
    // First popup: 10-20s after page load
    const firstDelay = Math.random() * (20000 - 10000) + 10000;
    const firstTimer = setTimeout(() => {
      const purchase = generatePurchase();
      setCurrentPurchase(purchase);
      setUsedNames(prev => [...prev, purchase.name]);
      setIsVisible(true);
      // Hide after 4-6s
      const visibleDuration = Math.random() * (6000 - 4000) + 4000;
      setTimeout(() => {
        setIsVisible(false);
        // Schedule next popup 9-18s AFTER hiding
        const scheduleNext = () => {
          const nextDelay = Math.random() * (18000 - 9000) + 9000;
          setTimeout(() => {
            if (!isClosed) {
              const nextPurchase = generatePurchase();
              setCurrentPurchase(nextPurchase);
              setUsedNames(prev => [...prev, nextPurchase.name]);
              setIsVisible(true);
              // Hide and schedule next
              const nextVisibleDuration = Math.random() * (6000 - 4000) + 4000;
              setTimeout(() => {
                setIsVisible(false);
                scheduleNext(); // Recursive scheduling
              }, nextVisibleDuration);
            }
          }, nextDelay);
        };
        scheduleNext();
      }, visibleDuration);
    }, firstDelay);
    return () => clearTimeout(firstTimer);
  }, [isClosed]);
  const handleClose = () => {
    setIsVisible(false);
    setIsClosed(true);
    sessionStorage.setItem('recentPurchasePopupClosed', 'true');
  };
  if (isClosed || !currentPurchase) return null;
  return <AnimatePresence data-id="element-159">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.4
    }} className="fixed bottom-4 left-4 z-50" style={{
      width: 'fit-content',
      maxWidth: '280px',
      minWidth: 0
    }} data-id="element-160">
          <div className="bg-[#2b0a3d] rounded-xl shadow-2xl border border-[#D4AF37]/20 relative inline-flex" data-id="element-161">
            {/* Close button - absolute positioned */}
            <button onClick={handleClose} className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center text-white/60 hover:text-white transition-colors z-10" style={{
          position: 'absolute',
          top: '10px',
          right: '10px'
        }} data-id="element-162">
              <X className="w-3.5 h-3.5" data-id="element-163" />
            </button>

            {/* Content with padding-right for X button */}
            <div style={{
          padding: '12px',
          paddingRight: '30px',
          minWidth: 0
        }} data-id="element-164">
              {/* Line 1: Header with green dot */}
              <div className="flex items-center gap-1.5 mb-1" data-id="element-165">
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" data-id="element-166"></div>
                <span className="text-[10px] font-semibold text-white/80 uppercase tracking-wide" style={{
              whiteSpace: 'nowrap'
            }} data-id="element-167">
                  Compra Reciente
                </span>
              </div>

              {/* Line 2: Name (bold, larger) with ellipsis */}
              <p className="text-base font-bold text-white mb-0.5 leading-tight" style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0
          }} data-id="element-168">
                {currentPurchase.name}
              </p>

              {/* Line 3: Details with price and ellipsis */}
              <p className="text-xs text-white/70 leading-tight" style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0
          }} data-id="element-169">
                Edición digital •{' '}
                <span className="text-[#D4AF37] font-semibold" data-id="element-170">
                  ${currentPurchase.price} USD
                </span>
              </p>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}